import { useCallback, useEffect, useMemo, useState } from 'react';
import { useChat } from '../context/ChatContext';
import { getDailyChatReport } from '../api/chat';

// BÁO CÁO CHAT NGÀY (rule-based): mỗi nhân viên 1 thẻ — bắt KH chưa trả lời / có dấu hiệu khiếu nại +
// thống kê chat KH & nội bộ. Bấm "Mở chat" để nhảy thẳng vào hội thoại khách.
const vnToday = () => new Date(Date.now() + 7 * 3600 * 1000).toISOString().slice(0, 10);
const fmtTime = (d) => { try { return new Date(d).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit', hour12: false }); } catch { return ''; } };
const fmtWait = (min) => { if (min < 60) return `${min}′`; const h = Math.floor(min / 60); return `${h}h${min % 60 ? (min % 60) + '′' : ''}`; };
const SVC = { kd: 'KD', cskh: 'CSKH', dvkh: 'DVKH' };

export default function ChatDailyReportPage() {
  const { deepOpenConv } = useChat();
  const [date, setDate] = useState(vnToday());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onlyAlerts, setOnlyAlerts] = useState(false);

  const load = useCallback((d) => {
    setLoading(true);
    getDailyChatReport(d).then((r) => setData(r)).catch(() => setData({ date: d, report: [] })).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(date); }, [date, load]);

  const openCust = (convId) => deepOpenConv && deepOpenConv(convId, 'customer');

  const rows = useMemo(() => {
    const list = (data?.report || []).filter((r) => r.hasActivity);
    return onlyAlerts ? list.filter((r) => r.alerts > 0) : list;
  }, [data, onlyAlerts]);

  const totals = useMemo(() => {
    const r = data?.report || [];
    return {
      unanswered: r.reduce((s, x) => s + x.customer.unanswered.length, 0),
      complaints: r.reduce((s, x) => s + x.customer.complaints.length, 0),
      people: r.filter((x) => x.hasActivity).length,
    };
  }, [data]);

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h2>Báo cáo chat ngày</h2>
          <div className="sub">Tổng hợp theo nhân viên · tự bắt khách chưa trả lời / có dấu hiệu khiếu nại (không dùng AI)</div>
        </div>
        <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="date" value={date} max={vnToday()} onChange={(e) => setDate(e.target.value || vnToday())} />
          <button className="btn sm ghost" onClick={() => load(date)}>⟳ Tính lại</button>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 14 }}>
        <div className="row between" style={{ flexWrap: 'wrap', gap: 10 }}>
          <div className="row" style={{ gap: 20, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <span className="small muted"><b className="mono" style={{ fontSize: 18, color: 'var(--danger)' }}>{totals.unanswered}</b> khách chờ trả lời</span>
            <span className="small muted"><b className="mono" style={{ fontSize: 18, color: 'var(--warn)' }}>{totals.complaints}</b> có dấu hiệu khiếu nại</span>
            <span className="small muted"><b className="mono" style={{ fontSize: 18 }}>{totals.people}</b> nhân viên có hoạt động</span>
          </div>
          <label className="row" style={{ gap: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={onlyAlerts} onChange={(e) => setOnlyAlerts(e.target.checked)} style={{ width: 'auto' }} />
            <span className="small">Chỉ hiện người cần chú ý</span>
          </label>
        </div>
      </div>

      {loading ? (
        <div className="card"><div className="small muted" style={{ padding: 8 }}>Đang tính…</div></div>
      ) : !rows.length ? (
        <div className="card"><div className="empty">Không có hoạt động chat trong ngày này.</div></div>
      ) : (
        <div style={{ display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))' }}>
          {rows.map((r) => (
            <div key={r.user.id} className="card chatrep-card">
              <div className="chatrep-head">
                <div>
                  <b>{r.user.name}</b>{r.user.employeeCode ? <code className="small" style={{ marginLeft: 6, color: 'var(--warn)' }}>{r.user.employeeCode}</code> : null}
                  {r.user.department ? <span className="small muted"> · {r.user.department}</span> : null}
                </div>
                {r.alerts > 0 && <span className="chatrep-badge">⚠ {r.alerts}</span>}
              </div>

              <div className="chatrep-sec-title">KHÁCH HÀNG · {r.customer.convCount} hội thoại · {r.customer.inCount + r.customer.outCount} tin ({r.customer.inCount} vào / {r.customer.outCount} ra)</div>
              {r.customer.unanswered.length > 0 && (
                <div className="chatrep-block">
                  <div className="chatrep-block-title danger">🔴 Khách CHƯA được trả lời ({r.customer.unanswered.length})</div>
                  {r.customer.unanswered.slice(0, 8).map((x, i) => (
                    <div key={i} className="chatrep-item">
                      <span><code>{x.custCode || '—'}</code> {x.custName ? x.custName : ''} <span className="small muted">· tin cuối {fmtTime(x.lastInAt)} (chờ {fmtWait(x.waitMin)})</span></span>
                      <button type="button" className="btn xs ghost" onClick={() => openCust(x.convId)}>Mở chat</button>
                    </div>
                  ))}
                  {r.customer.unanswered.length > 8 && <div className="small muted">… và {r.customer.unanswered.length - 8} khách khác</div>}
                </div>
              )}
              {r.customer.complaints.length > 0 && (
                <div className="chatrep-block">
                  <div className="chatrep-block-title warn">⚠️ Có thể khiếu nại ({r.customer.complaints.length}) — khớp từ khoá</div>
                  {r.customer.complaints.slice(0, 8).map((x, i) => (
                    <div key={i} className="chatrep-item">
                      <span><code>{x.custCode || '—'}</code> <span className="chatrep-kw">“{x.kw}”</span> <span className="small muted">{x.text}</span></span>
                      <button type="button" className="btn xs ghost" onClick={() => openCust(x.convId)}>Mở chat</button>
                    </div>
                  ))}
                </div>
              )}
              {!r.customer.unanswered.length && !r.customer.complaints.length && r.customer.convCount > 0 && (
                <div className="small" style={{ color: 'var(--ok)', margin: '2px 0 6px' }}>✓ Không có khách chờ / khiếu nại</div>
              )}

              <div className="chatrep-sec-title">NỘI BỘ · {r.internal.groups} nhóm · gửi {r.internal.sent} tin</div>
              <div className="small muted" style={{ marginBottom: 2 }}>
                📋 Công việc: được giao {r.internal.assignedToday} · xong {r.internal.doneToday}
                {r.internal.mentions > 0 ? <> · 🔔 bị nhắc tên (@) {r.internal.mentions}</> : null}
              </div>

              <div className="chatrep-foot">➡ {r.customer.unanswered.length} khách chờ trả lời, {r.customer.complaints.length} khách có dấu hiệu khiếu nại.</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
